import React from 'react'
import styled from '@emotion/styled'
import { useToggle } from 'view/hooks'
import * as mixins from 'view/styles/mixins'
import * as constants from 'view/styles/constants'
import { DropdownListbox, OptionKey, OptionTemplateProps } from '../options'
import { MenuContainer } from './MenuContainer'

export interface DropdownAction {
  id?: string
  onClick: () => unknown
  enabled?: boolean
  warnings?: string[]
  label: string
}

export interface DropdownOptionsProps<TAction extends DropdownAction> {
  'data-test-id'?: string
  actions: TAction[]
  children: ({ toggle, isOpen }: { toggle: () => void; isOpen: boolean }) => JSX.Element
  className?: string
  disabled?: boolean
  showDisabledActions?: boolean
  withPortal?: boolean
}

export const ActionItemTemplate = styled.span<OptionTemplateProps<any, OptionKey>>`
  ${mixins.noWrap}
  font-weight: ${constants.FontWeight.Bold};
  font-size: 12px;
  width: 100%;
  cursor: pointer;
  text-transform: capitalize;
  display: flex;
  align-items: center;
  border-radius: 3px;
  padding: 7px 9px;
  height: 26px;
  color: ${constants.CORE_PRIMARY_7};
  &:hover {
    background-color: ${constants.CORE_PRIMARY_1};
  }
`

const MenuContainerStyled = styled(MenuContainer)`
  width: 100%;
`

/**
 * Base component for creating buttons with
 * dropdown functionality. It uses children props
 * to pass the toggle function and isOpen state.
 */
export const DropdownOptions = <TAction extends DropdownAction>({
  'data-test-id': dataTestId,
  actions,
  children,
  className,
  disabled,
  showDisabledActions,
  withPortal
}: DropdownOptionsProps<TAction>) => {
  const [isOpen, toggle] = useToggle(false)
  const actionFilter = (_actionKey: number, action: TAction) => showDisabledActions || action.enabled !== false

  return (
    <MenuContainerStyled
      className={className}
      disabled={disabled}
      primaryComponent={children({ toggle, isOpen })}
      menuVisible={isOpen}
      onUpdateMenuVisible={v => toggle(v)}
      withPortal={withPortal}
      dataTestId={dataTestId}>
      <DropdownListbox
        optionKeys={actions.map((_action, idx) => idx)}
        getOption={(actionIdx: number) => actions[actionIdx]}
        getOptionText={action => action.label}
        getOptionTooltip={action => (action.warnings?.length ? action.warnings.join('') : '')}
        getOptionDisabled={action => action.enabled === false}
        onOptionClick={onOptionClick}
        optionTemplate={renderActionItem}
        filterOption={actionFilter}
      />
    </MenuContainerStyled>
  )
}

function renderActionItem(optionProps: OptionTemplateProps<any, OptionKey>) {
  return <ActionItemTemplate {...optionProps}>{optionProps.optionText}</ActionItemTemplate>
}

function onOptionClick<T extends DropdownAction>(_actionIdx: number, action: T, _evt: React.MouseEvent) {
  action.onClick()
}
